import type { WebhookVariant } from '../../api/webhooks.ts'

import { VARIANT_LABELS } from './format.ts'

// The webhook type registry: what kinds of webhooks exist, and which
// production/sandbox variant each one owns.
//
// A `Record` keyed by `WebhookTypeId` rather than a flat list of variants so
// that adding a webhook type is a data change, not a rewrite of every place
// that switches on a variant (precedent: SECTION_BY_TREE in
// src/shell/destinations.ts).

export type WebhookTypeId = 'deliveries' | 'payloads'
export type WebhookEnvironment = 'production' | 'sandbox'

export interface WebhookTypeDef {
  label: string
  description: string
  variants: Record<WebhookEnvironment, WebhookVariant>
  signed: boolean
  docs: { setup: string; reference: string }
  /** An extra caveat shown under the type's description, if it has one. */
  note?: string
}

export const WEBHOOK_TYPES = {
  deliveries: {
    label: 'Delivery webhooks',
    description:
      'Where Curri sends delivery updates: the full delivery snapshot on every status change.',
    variants: { production: 'deliveries', sandbox: 'sandboxDeliveries' },
    signed: false,
    docs: {
      setup: '/docs/webhooks/deliveries/setup',
      reference: '/docs/webhooks/delivery-statuses',
    },
  },
  payloads: {
    label: 'Payload webhooks',
    description:
      'Where Curri sends payload events: a signed event as each payload is created, amended or changes status.',
    variants: { production: 'payloads', sandbox: 'sandboxPayloads' },
    signed: true,
    docs: {
      setup: '/docs/webhooks/payloads/setup',
      reference: '/docs/webhooks/payloads/events',
    },
    note: 'An endpoint configured by a parent account also receives your payload events and is not listed here.',
  },
} as const satisfies Record<WebhookTypeId, WebhookTypeDef>

export const WEBHOOK_TYPE_IDS: readonly WebhookTypeId[] = Object.keys(
  WEBHOOK_TYPES
) as WebhookTypeId[]

const buildVariantInfo = (): Record<
  WebhookVariant,
  { type: WebhookTypeId; environment: WebhookEnvironment }
> => {
  const info = {} as Record<
    WebhookVariant,
    { type: WebhookTypeId; environment: WebhookEnvironment }
  >
  for (const type of WEBHOOK_TYPE_IDS) {
    const variants = WEBHOOK_TYPES[type].variants
    info[variants.production] = { type, environment: 'production' }
    info[variants.sandbox] = { type, environment: 'sandbox' }
  }
  return info
}

export const VARIANT_INFO = buildVariantInfo()

export const VARIANTS: readonly WebhookVariant[] = Object.keys(
  VARIANT_INFO
) as WebhookVariant[]

export const variantsOf = (type: WebhookTypeId): readonly WebhookVariant[] => {
  const variants = WEBHOOK_TYPES[type].variants
  return [variants.production, variants.sandbox]
}

export const isVariant = (value: string | undefined): value is WebhookVariant =>
  value !== undefined && Object.hasOwn(VARIANT_INFO, value)

export const isWebhookTypeId = (
  value: string | null | undefined
): value is WebhookTypeId =>
  value !== null && value !== undefined && Object.hasOwn(WEBHOOK_TYPES, value)

export const variantTone = (variant: WebhookVariant): 'info' | 'neutral' =>
  VARIANT_INFO[variant].environment === 'production' ? 'info' : 'neutral'

export const isSignedVariant = (variant: WebhookVariant): boolean =>
  WEBHOOK_TYPES[VARIANT_INFO[variant].type].signed

export const describeVariant = (variant: WebhookVariant): string =>
  `${WEBHOOK_TYPES[VARIANT_INFO[variant].type].label} · ${VARIANT_LABELS[variant]}`

// Every variant `WebhookVariant` can hold must come from some webhook type's
// `variants` map — this fails to compile the moment that stops being true,
// rather than leaving a variant that `VARIANT_INFO` silently can't resolve.
type Covered = (typeof WEBHOOK_TYPES)[WebhookTypeId]['variants'][WebhookEnvironment]
export const everyVariantHasAType: Exclude<WebhookVariant, Covered> extends never
  ? true
  : never = true
